import { gql } from '@apollo/client';

// Query to fetch all EWR reports (matching Retool schema)
export const GET_EWR_REPORT_HISTORY = gql`
  query Reports {
    ewrReports {
      id
      displayName
      createUid {
        id
        name
      }
      createDate
      reportDate
      asOfDate
      leveragePeriodEnd
      generationStatus
      reportLogs {
        step
        timestamp
        context
      }
      errorMessage
    }
  }
`;

// Mutation to create a new EWR report
export const CREATE_EWR_REPORT = gql`
  mutation CreateEwrReport(
    $reportDate: Date!
    $asOfDate: Date!
    $leveragePeriodEnd: Date
  ) {
    createEwrReport(
      reportDate: $reportDate
      asOfDate: $asOfDate
      leveragePeriodEnd: $leveragePeriodEnd
    ) {
      ok
      reportId
      message
    }
  }
`;

// Mutation to get/download an EWR report (matching Retool schema)
export const GET_EWR_REPORT = gql`
  mutation getEwrReport($reportId: ID!) {
    getEwrReport(reportId: $reportId) {
      ok
      data
      filename
      message
      lastUpdatedSync
      report {
        id
        reportDate
        asOfDate
        leveragePeriodEnd
        lastUpdatedSync
        companyDataIds {
          id
          name
          dealTeam
          rating
          smcExposure
        }
      }
    }
  }
`;

export interface CompanyData {
  dealTeam?: null | string;
  id: string;
  name: string;
  rating?: null | string;
  smcExposure?: null | string;
}

export interface CreateEwrReportResponse {
  createEwrReport: {
    message: string;
    ok: boolean;
    reportId?: string;
  };
}

export interface EWRReportHistoryNode {
  asOfDate: string;
  createDate: string;
  createUid: {
    id: string;
    name: string;
  };
  displayName: string;
  errorMessage?: null | string;
  generationStatus?: string; // pending, in_progress, completed, or failed
  id: string;
  leveragePeriodEnd?: null | string;
  reportDate: string;
  // For backward compatibility and to use id as reportId
  reportId?: null | string;
  reportLogs?: ReportLogEntry[];
}

export interface GetEwrReportHistoryResponse {
  ewrReports: EWRReportHistoryNode[];
}

export interface GetEwrReportResponse {
  getEwrReport: {
    data?: string; // Base64 encoded PDF
    filename?: string;
    lastUpdatedSync?: null | string;
    message: string;
    ok: boolean;
    report?: {
      asOfDate: string;
      companyDataIds?: CompanyData[];
      id: string;
      lastUpdatedSync?: null | string;
      leveragePeriodEnd?: null | string;
      reportDate: string;
    };
  };
}

// Types for the GraphQL responses
export interface ReportLogEntry {
  context?: null | string;
  step: string;
  timestamp: string;
}

// ==================== PID REPORT QUERIES ====================

// Query to fetch iLevel assets for dropdown selection
export const GET_ILEVEL_ASSETS = gql`
  query GetIlevelAssets($name: String, $status: String, $industry: String) {
    ilevelAssets(name: $name, status: $status, industry: $industry) {
      edges {
        node {
          id
          name
          ilevelId
          excelTicker
          status
          assetType
          industry
          geography
          watchlistStatus
          acquisitionDate
          exitDate
          dealLead
          sponsorName
          shortDescription
        }
      }
    }
  }
`;

// Query to fetch all PID reports history
export const GET_PID_REPORT_HISTORY = gql`
  query GetPidReportHistory {
    pidReportHistory {
      id
      generatedDate
      generatedBy {
        id
        name
        email
      }
      reportId {
        id
        name
        investmentName
        financialsPeriodEnd
        periodEnd
        asOfDate
      }
      generationStatus
      reportLogs {
        step
        timestamp
        context
      }
      errorMessage
    }
  }
`;

// Mutation to create a new PID report
export const CREATE_PID_REPORT = gql`
  mutation CreatePidReport(
    $assetId: ID!
    $financialsPeriodEnd: Date!
    $periodEnd: Date!
    $asOfDate: Date!
  ) {
    createPidReport(
      assetId: $assetId
      financialsPeriodEnd: $financialsPeriodEnd
      periodEnd: $periodEnd
      asOfDate: $asOfDate
    ) {
      ok
      reportId
      message
    }
  }
`;

// Mutation to get/download a PID report
export const GET_PID_REPORT = gql`
  mutation GetPidReport($reportId: ID!) {
    getPidReport(reportId: $reportId) {
      ok
      data
      filename
      message
    }
  }
`;

// Mutation to poll for report generation status and logs (EWR + PID)
export const GET_REPORT_STATUS = gql`
  mutation GetReportStatus($reportId: ID!, $reportType: String!) {
    getReportStatus(reportId: $reportId, reportType: $reportType) {
      ok
      generationStatus
      reportLogs {
        step
        timestamp
        context
      }
      errorMessage
      message
    }
  }
`;

// ==================== PID REPORT TYPES ====================

export interface CreatePidReportResponse {
  createPidReport: {
    message: string;
    ok: boolean;
    reportId?: string;
  };
}

export interface GetILevelAssetsResponse {
  ilevelAssets: {
    edges: {
      node: ILevelAssetNode;
    }[];
  };
}

export interface GetPidReportHistoryResponse {
  pidReportHistory: PIDReportHistoryNode[];
}

export interface GetPidReportResponse {
  getPidReport: {
    data?: string; // Base64 encoded PDF
    filename?: string;
    message: string;
    ok: boolean;
  };
}

export interface GetReportStatusResponse {
  getReportStatus: {
    errorMessage?: null | string;
    generationStatus: string;
    message?: null | string;
    ok: boolean;
    reportLogs: ReportLogEntry[];
  };
}

export interface ILevelAssetNode {
  acquisitionDate: null | string;
  assetType: string;
  dealLead: null | string;
  excelTicker: string;
  exitDate: null | string;
  geography: string;
  id: string;
  ilevelId: string;
  industry: string;
  name: string;
  shortDescription: null | string;
  sponsorName: null | string;
  status: string;
  watchlistStatus: string;
}

export interface PIDReportHistoryNode {
  errorMessage?: null | string;
  generatedBy: {
    email: string;
    id: string;
    name: string;
  };
  generatedDate: string;
  generationStatus?: string; // pending, in_progress, completed, or failed
  id: string;
  reportId: {
    asOfDate: string;
    financialsPeriodEnd?: string;
    id: string;
    investmentName: string;
    name: string;
    periodEnd: string;
  };
  reportLogs?: ReportLogEntry[];
}

// Query to get queue position and estimated time for report generation
export const GET_QUEUE_POSITION = gql`
  query GetQueuePosition($reportType: String) {
    sierraReportPosition(reportType: $reportType) {
      position
      totalJobs
      message
      estimatedTimeMinutes
    }
  }
`;

export interface GetQueuePositionResponse {
  sierraReportPosition: QueuePositionData;
}

export interface QueuePositionData {
  estimatedTimeMinutes: null | number;
  message: string;
  position: number;
  totalJobs: number;
}

// ==================== LOAN TAPE REPORT QUERIES ====================

// Query to get available loan tape funds
export const GET_LOAN_TAPE_FUNDS = gql`
  query GetLoanTapeFunds {
    loanTapeFunds {
      id
      name
      disabled
    }
  }
`;

export const CREATE_LOAN_TAPE_REPORT = gql`
  mutation CreateLoanTapeReport(
    $fundId: ID
    $periodEnd: Date!
    $asOfDate: Date!
    $securityStatusDate: Date!
    $borrowerStatusDate: Date!
  ) {
    createLoanTapeReport(
      fundId: $fundId
      periodEnd: $periodEnd
      asOfDate: $asOfDate
      securityStatusDate: $securityStatusDate
      borrowerStatusDate: $borrowerStatusDate
    ) {
      ok
      reportId
      message
      report {
        id
        name
        fundId
        fundName
        periodEnd
        generationStatus
      }
    }
  }
`;

// Mutation to get/download a Loan Tape report (Excel)
export const GET_LOAN_TAPE_REPORT = gql`
  mutation GetLoanTapeReport($reportId: ID!) {
    getLoanTapeReport(reportId: $reportId) {
      ok
      data
      filename
      message
    }
  }
`;

export const GET_LOAN_TAPE_REPORT_HISTORY = gql`
  query GetLoanTapeReports {
    loanTapeReports {
      id
      name
      fundId
      fundName
      periodEnd
      asOfDate
      securityStatusDate
      borrowerStatusDate
      generatedDate
      generationStatus
      errorMessage
    }
  }
`;

export interface CreateLoanTapeReportResponse {
  createLoanTapeReport: {
    message: string;
    ok: boolean;
    report?: {
      fundId: string;
      fundName: string;
      generationStatus: string;
      id: string;
      name: string;
      periodEnd: string;
    };
    reportId?: string;
  };
}

export interface GetLoanTapeFundsResponse {
  loanTapeFunds: LoanTapeFund[];
}

export interface GetLoanTapeReportHistoryResponse {
  loanTapeReports: LoanTapeReportHistoryNode[];
}

export interface GetLoanTapeReportResponse {
  getLoanTapeReport: {
    data?: string; // Base64 encoded Excel
    filename?: string;
    message: string;
    ok: boolean;
  };
}

export interface LoanTapeFund {
  id: string;
  name: string;
  disabled: boolean;
}

export interface LoanTapeReportHistoryNode {
  asOfDate: string;
  borrowerStatusDate?: null | string;
  errorMessage?: null | string;
  fundId: string;
  fundName: string;
  generatedDate?: null | string;
  generationStatus?: string; // pending, in_progress, completed, or failed
  id: string;
  name: string;
  periodEnd: string;
  securityStatusDate?: null | string;
}
